import { useDeclareReloadSafe } from "@simplicity/ui";
import { useCallback, useState } from "react";

/**
 * Tracks whether form fields have changed from their initial values.
 * Returns `isDirty` (true when any field differs from baseline),
 * `resetBaseline` (call after successful save to set new baseline),
 * and `updateBaseline` (merge partial updates into the baseline).
 */
export function useDirtyForm<T extends Record<string, string>>(current: T) {
  const [baseline, setBaselineState] = useState<T | null>(null);

  // Set baseline when data first loads
  const setBaseline = useCallback((values: T) => {
    setBaselineState({ ...values });
  }, []);

  // Reset baseline after save (snapshot current values)
  const resetBaseline = useCallback(() => {
    setBaselineState({ ...current });
  }, [current]);

  // Merge partial updates into the baseline (e.g. after tab-switch re-serialization)
  const updateBaseline = useCallback((partial: Partial<T>) => {
    setBaselineState((prev) => (prev ? { ...prev, ...partial } : null));
  }, []);

  // Compare current values to baseline
  const isDirty =
    baseline !== null && Object.keys(current).some((key) => current[key] !== baseline[key]);

  // SIM-1670: report dirty state to the release auto-reload gate (LD-1) so a
  // deploy never reloads over a form with unsaved edits. No-op outside a
  // DirtyFormProvider (e.g. in isolated unit tests).
  useDeclareReloadSafe(!isDirty);

  return { isDirty, setBaseline, resetBaseline, updateBaseline };
}
